<script setup>
defineOptions({
  name: 'SocialReturnAgreementsContractSelectionModal',
});

/*
|--------------------------------------------------------------------------
| SocialReturnAgreementsContractSelectionModal.vue
|--------------------------------------------------------------------------
| Purpose
|   Modal for selecting a contract from the "Social Return Agreements" eligible list.
|   Used by the dashboard tile to navigate to the Social Return Agreements workflow.
|
| Usage (parent):
|   <SocialReturnAgreementsContractSelectionModal v-model="showModal" @select="onSelect" />
|
| Props
|   - modelValue (Boolean): controls modal visibility via v-model
|
| Events
|   - update:modelValue(Boolean): emitted when the modal opens/closes
|   - select(Object): emitted when the user chooses a contract
|   - close: emitted when modal is closed
|
| Workflow
|   1) Setup & routing helper
|   2) Props & emits
|   3) Reactive state
|   4) Methods: handleSelect, close
*/

import { ref, computed, getCurrentInstance } from 'vue'
import { router } from '@inertiajs/vue3'
import Modal from '@/components/Modal.vue'
import FormRichSelect from '@/components/Form/FormRichSelect.vue'

/* Routing helper (Ziggy route) */
const instance = getCurrentInstance()
const route = instance?.appContext.config.globalProperties.route

/* Props & Emits */
const props = defineProps({
  /** Controls modal visibility - accepts boolean or truthy value (e.g. tile key) */
  modelValue: { type: [Boolean, String], default: false },
})

const emit = defineEmits(['update:modelValue', 'select', 'close'])

/* v-model adapter for modal visibility - converts to boolean for Modal component */
const show = computed({
  get: () => !!props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

/* Reactive state */
const selectedContract = ref(null)

/* Search URL for FormRichSelect remote search */
const searchUrl = computed(() => route('contracts.social-return-agreements.data'))

/**
 * Handle contract selection
 * Redirects to Social Return Agreements step 1 for selected contract
 *
 * @route GET /contracts/{contract}/sr-afspraken/stap-{stepNumber}-{slug}
 */
function handleSelect() {
  if (!selectedContract.value) return

  const contractId = selectedContract.value
  // Step 1 is always the first visible step with slug 'contactpersonen'
  const url = route('contracts.social-return-agreements.step', {
    contract: contractId,
    stepNumber: 1,
    slug: 'contactpersonen',
  })

  emit('select', { contract_id: contractId })
  router.visit(url)
  close()
}

/**
 * Close the modal and reset state
 */
function close() {
  show.value = false
  selectedContract.value = null
  emit('close')
}
</script>

<template>
  <Modal
    :show="show"
    title="SR Afspraken invoeren"
    size="lg"
    theme="orange"
    confirm-button-variant="orange"
    :confirm-button-disabled="!selectedContract"
    :show-default-footer="true"
    @close="close"
    @cancel="close"
    @confirm="handleSelect"
    :cancel-button-id="'input-social-return-agreements-modal-cancel'"
    :confirm-button-id="'input-social-return-agreements-modal-select'"
  >
    <div class="social-return-agreements-selection-modal-content">
      <!-- Description -->
      <p class="modal-description">
        Selecteer een contract om SR afspraken in te voeren.
      </p>

      <!-- Contract Dropdown -->
      <div class="social-return-agreements-selection-field">
        <label for="input-social-return-agreements-contract-selection" class="form-label">
          Contract
        </label>
        <FormRichSelect
          id="input-social-return-agreements-contract-selection"
          v-model="selectedContract"
          :search-url="searchUrl"
          placeholder="Zoek op titel, klant of opdrachtnemer..."
          option-label="label"
          option-value="value"
          option-layout="card"
          class="wide-dropdown"
        />
      </div>
    </div>
  </Modal>
</template>